import {
  analyticsApi,
  integrationApi,
  projectApi,
  type GoalAnalyticsSummary,
  type Integration,
  type ProjectAnalyticsResponse,
  type ProjectGoal,
  type RevenueCurrencySummary,
  type RevenueProvider,
  type RevenueSubscriptionLifecycle,
} from '@shared/api-client';
import { analyticsRangeLabel } from '@shared/api-client/analytics-range';
import { useQuery } from '@tanstack/react-query';
import { Check, Clock3, RefreshCw, UserRoundPlus } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { ChangeIndicator } from '@/components/analytics/change-indicator';
import {
  detectGrowthAnomalies,
  growthAnomalyPrompt,
  type GrowthAnomaly,
} from '@/components/analytics/growth-anomalies';
import { GrowthChart } from '@/components/analytics/growth-chart';
import {
  buildGrowthPoints,
  type GrowthPoint,
} from '@/components/analytics/growth-model';
import type { ChartMetric } from '@/components/analytics/line-chart';
import { useAssistant } from '@/components/assistant/assistant-context';
import { Card } from '@/components/ui/card';
import { HelpTooltip } from '@/components/ui/help-tooltip';
import {
  cn,
  formatCompactNumber,
  formatCurrencyMinor,
  formatRelative,
} from '@/lib/utils';
import { RevenueOverview } from './revenue-overview';
import { GoalsReport } from './goals-report';

const TRAFFIC_METRICS: Array<{ id: ChartMetric; label: string }> = [
  { id: 'visitors', label: 'Visitors' },
  { id: 'sessions', label: 'Sessions' },
  { id: 'pageviews', label: 'Pageviews' },
  { id: 'events', label: 'All events' },
  { id: 'customEvents', label: 'Product events' },
  { id: 'identifiedUsers', label: 'Identified users' },
];

type RevenueIntegration = Integration & { provider: RevenueProvider };

const isRevenueIntegration = (
  integration: Integration,
): integration is RevenueIntegration =>
  integration.provider === 'revenuecat' || integration.provider === 'stripe';

export type GrowthCardProps = {
  points: GrowthPoint[];
  visitors: number;
  previousVisitors: number;
  bucketMinutes: number;
  revenue?: RevenueCurrencySummary;
  currentState?: Pick<
    RevenueCurrencySummary,
    | 'currency'
    | 'mrrMinor'
    | 'activeSubscribers'
    | 'activeTrials'
    | 'lastPaymentAt'
  >;
  subscriptionLifecycle?: RevenueSubscriptionLifecycle;
  comparisonEnabled?: boolean;
  showRevenueMetrics?: boolean;
  revenueStatus: string;
  milestoneStatus?: string;
  paidSince?: string | null;
  provider?: string;
  periodLabel?: string;
  revenuePeriodLabel?: string;
  syncDetails?: string;
  primaryGoal?: ProjectGoal;
  goalSummary?: GoalAnalyticsSummary;
  goals?: ProjectGoal[];
  selectedGoalId?: string;
  onGoalChange?: (goalId: string) => void;
  onExplainAnomaly?: (anomaly: GrowthAnomaly) => void | Promise<void>;
  isExplainingAnomaly?: boolean;
};

export const GrowthCard = ({
  points,
  visitors,
  previousVisitors,
  bucketMinutes,
  revenue,
  currentState = revenue,
  subscriptionLifecycle,
  comparisonEnabled = true,
  showRevenueMetrics = true,
  revenueStatus,
  milestoneStatus,
  paidSince,
  provider = 'RevenueCat',
  periodLabel = 'Selected period',
  revenuePeriodLabel = periodLabel,
  syncDetails,
  primaryGoal,
  goalSummary,
  goals = primaryGoal ? [primaryGoal] : [],
  selectedGoalId = primaryGoal?.id,
  onGoalChange,
  onExplainAnomaly,
  isExplainingAnomaly = false,
}: GrowthCardProps) => {
  const [metric, setMetric] = React.useState<ChartMetric>('visitors');
  const [showSignups, setShowSignups] = React.useState(true);
  const [showPaid, setShowPaid] = React.useState(true);
  const [showRevenue, setShowRevenue] = React.useState(false);
  const observedGoalCount = points.reduce(
    (sum, point) => sum + (point.signups ?? 0),
    0,
  );
  const paidCount = points.some((point) => point.newPaid !== null)
    ? points.reduce((sum, point) => sum + (point.newPaid ?? 0), 0)
    : null;
  const signupAvailable = points.some((point) => point.signups !== null);
  const revenueAvailable = points.some(
    (point) => point.recordedRevenueUsd !== null,
  );
  const paymentsView = showRevenue && revenueAvailable;
  const metricLabel = TRAFFIC_METRICS.find((item) => item.id === metric)!.label;
  const goalLabel = primaryGoal?.name ?? 'Signups';
  const anomalies = React.useMemo(
    () =>
      detectGrowthAnomalies(points, {
        bucketMinutes,
        goalLabel,
      }),
    [bucketMinutes, goalLabel, points],
  );
  const goalCount = goalSummary?.conversions ?? observedGoalCount;
  const goalHelp = primaryGoal
    ? `Unique visitors who completed ${goalLabel.toLowerCase()} in ${periodLabel.toLowerCase()}. ${primaryGoal.kind === 'event' ? `Triggered by the ${primaryGoal.eventName} event.` : `Triggered by ${primaryGoal.pathMatch === 'prefix' ? 'a page path starting with' : 'the page path'} ${primaryGoal.pathPattern}.`}`
    : 'Unique visitors who sent signup_completed after an account was created.';
  const conversionRate =
    goalSummary?.conversionRate ??
    (visitors > 0 && signupAvailable ? (goalCount / visitors) * 100 : null);
  const formatRate = (value: number | null) =>
    value === null
      ? '—'
      : `${value < 10 ? value.toFixed(1) : value.toFixed(0)}%`;
  const paidHelp = `Paid initial purchases and trial-to-paid conversions received through RevenueCat webhooks${paidSince ? ` since ${new Date(paidSince).toLocaleDateString(undefined, { timeZone: 'UTC', month: 'short', day: 'numeric' })}` : ''}. Excludes regular renewals and one-time purchases. History before connection is not included.`;
  const paymentHelp =
    'Payment bars show recorded RevenueCat webhook payments in USD, including renewals and one-time purchases. They do not include the full imported revenue history.';
  const totals = [
    {
      label: 'Visitors',
      value: formatCompactNumber(visitors),
      help: `Website visitors in ${periodLabel.toLowerCase()}${comparisonEnabled ? ', compared with the preceding period' : ', including available imported traffic'}. Traffic and app activity share dates, not verified user identities.`,
      note: undefined,
      color: 'bg-accent',
      current: visitors,
      previous: previousVisitors,
    },
    {
      label: goalLabel,
      value: signupAvailable ? formatCompactNumber(goalCount) : '—',
      help: goalHelp,
      note: undefined,
      color: 'bg-accent',
      current: goalSummary?.conversions,
      previous: goalSummary?.previousConversions,
    },
    {
      label: 'Conversion',
      value: formatRate(conversionRate),
      help: `${goalLabel} divided by visitors in ${periodLabel.toLowerCase()}. Native events count each visitor once; imported history uses the provider’s daily unique totals.`,
      note: undefined,
      color: 'bg-accent',
      current: goalSummary?.conversionRate ?? undefined,
      previous: goalSummary?.previousConversionRate ?? undefined,
    },
    {
      label: 'New paid',
      value: paidCount === null ? '—' : formatCompactNumber(paidCount),
      help: paidHelp,
      note: paidCount === null ? 'No paid data' : undefined,
      color: 'bg-positive',
    },
    {
      label: 'Revenue',
      value: revenue
        ? formatCurrencyMinor(
            revenue.grossCollectedMinor,
            revenue.currency,
            true,
          )
        : '—',
      help: `Revenue reported by ${provider} for ${revenuePeriodLabel.toLowerCase()}${comparisonEnabled ? ', compared with the preceding period' : ''}. Includes subscription and one-time purchases. This is the provider’s total, not the sum of recorded payment bars.`,
      note: revenue ? undefined : revenueStatus,
      color: 'bg-positive',
      current: revenue?.grossCollectedMinor,
      previous: revenue?.previousGrossCollectedMinor,
    },
  ];
  const visibleTotals = showRevenueMetrics ? totals : totals.slice(0, 3);
  return (
    <Card className="@container overflow-hidden shadow-none">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-5 py-4 sm:px-6">
        <div className="flex items-center gap-1.5">
          <h2 className="text-base font-semibold text-ink">Traffic & growth</h2>
          <HelpTooltip label="About the growth chart">
            The blue line uses the traffic scale on the left. Green bars show
            new paid starts on the right scale. Goal markers sit on the traffic
            line to share the date, not the traffic value. Hover or tap for
            daily details. Payments switches the bars to recorded USD amounts.
            Missing values stay blank; recorded zeros appear in the tooltip.
            Dates use UTC, and the first and last buckets may be partial. This
            is not an identity-matched conversion funnel.
          </HelpTooltip>
        </div>
        <span className="text-xs text-ink-muted">
          {bucketMinutes < 1440
            ? 'Hourly'
            : bucketMinutes > 10080
              ? '4-weekly'
              : bucketMinutes > 1440
                ? 'Weekly'
                : 'Daily'}{' '}
          · UTC
        </span>
      </div>
      <dl
        className={cn(
          'grid grid-cols-2 border-b border-border',
          showRevenueMetrics ? '@xl:grid-cols-5' : '@xl:grid-cols-3',
        )}
      >
        {visibleTotals.map((item) => (
          <div key={item.label} className="min-w-0 px-5 py-4 sm:px-6">
            <dt className="flex items-center gap-2 text-xs font-medium text-ink-muted">
              <span className={cn('size-1.5 rounded-full', item.color)} />
              {item.label}
              <HelpTooltip
                label={`About ${item.label.toLowerCase()}`}
                className="-ml-1"
              >
                {item.help}
              </HelpTooltip>
            </dt>
            <dd className="mt-2 flex flex-wrap items-baseline gap-2">
              <span className="text-2xl font-semibold tracking-tight text-ink tabular-nums">
                {item.value}
              </span>
              {comparisonEnabled &&
                item.current !== undefined &&
                item.previous !== undefined && (
                  <ChangeIndicator
                    current={item.current}
                    previous={item.previous}
                  />
                )}
            </dd>
            {item.note && (
              <p className="mt-1 text-xs text-ink-muted">{item.note}</p>
            )}
          </div>
        ))}
      </dl>
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-6">
        <div className="flex flex-wrap items-center gap-2">
          <label className="sr-only" htmlFor="growth-traffic-metric">
            Traffic metric
          </label>
          <select
            id="growth-traffic-metric"
            value={metric}
            onChange={(event) => setMetric(event.target.value as ChartMetric)}
            className="h-8 rounded-md border border-border-strong bg-surface px-2 text-xs font-medium text-ink"
          >
            {TRAFFIC_METRICS.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>
          {goals.length > 1 && onGoalChange ? (
            <>
              <label className="sr-only" htmlFor="growth-goal">
                Goal
              </label>
              <select
                id="growth-goal"
                value={selectedGoalId}
                onChange={(event) => onGoalChange(event.target.value)}
                className="h-8 max-w-44 rounded-md border border-border-strong bg-surface px-2 text-xs font-medium text-ink"
              >
                {goals.map((goal) => (
                  <option key={goal.id} value={goal.id}>
                    {goal.name}
                  </option>
                ))}
              </select>
            </>
          ) : null}
          {[
            {
              label: goalLabel,
              value: showSignups,
              set: setShowSignups,
              available: signupAvailable,
              color: 'bg-accent',
            },
            {
              label: 'New paid',
              value: showPaid && !paymentsView,
              set: (value: boolean) => {
                setShowPaid(value);
                setShowRevenue(false);
              },
              available: paidCount !== null,
              color: 'bg-positive',
            },
            {
              label: 'Payments (USD)',
              value: paymentsView,
              set: setShowRevenue,
              available: revenueAvailable,
              color: 'bg-positive',
            },
          ]
            .filter((item) => item.available)
            .map((item) => (
              <React.Fragment key={item.label}>
                <button
                  type="button"
                  aria-pressed={item.value && item.available}
                  disabled={!item.available}
                  onClick={() => item.set(!item.value)}
                  className={cn(
                    'inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition-colors disabled:opacity-45',
                    item.value && item.available
                      ? 'border-border-strong bg-surface-muted text-ink'
                      : 'border-transparent text-ink-muted hover:bg-surface-muted',
                  )}
                >
                  {item.label === goalLabel ? (
                    <UserRoundPlus
                      aria-hidden="true"
                      className="size-3 text-accent"
                    />
                  ) : (
                    <span
                      className={cn(
                        'size-2',
                        item.label === 'New paid' ||
                          item.label === 'Payments (USD)'
                          ? 'rounded-sm'
                          : 'rounded-full',
                        item.color,
                      )}
                    />
                  )}
                  {item.label}
                  {item.value && item.available && <Check className="size-3" />}
                </button>
                {item.label === 'Payments (USD)' && (
                  <HelpTooltip label="About payment bars" className="-ml-2">
                    {paymentHelp}
                  </HelpTooltip>
                )}
              </React.Fragment>
            ))}
        </div>
      </div>
      <GrowthChart
        points={points}
        metric={metric}
        metricLabel={metricLabel}
        bucketMinutes={bucketMinutes}
        showSignups={showSignups && signupAvailable}
        showPaid={showPaid && paidCount !== null}
        showRevenue={paymentsView}
        showAppUsers={false}
        goalLabel={goalLabel}
        anomalies={anomalies}
        onExplainAnomaly={onExplainAnomaly}
        isExplainingAnomaly={isExplainingAnomaly}
      />
      {milestoneStatus && (
        <p role="status" className="px-6 pb-4 text-xs text-ink-muted">
          {milestoneStatus}
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-3 text-xs sm:px-6">
        {currentState ? (
          <p className="flex flex-wrap items-center gap-x-5 gap-y-2 text-ink-muted">
            <span className="inline-flex items-center gap-1 whitespace-nowrap">
              MRR now
              <strong className="ml-1 font-semibold tabular-nums text-ink">
                {formatCurrencyMinor(
                  currentState.mrrMinor,
                  currentState.currency,
                )}
              </strong>
              <HelpTooltip label="About MRR">
                Current monthly recurring revenue reported by the provider. It
                excludes one-time and lifetime purchases and does not change
                with the selected date range.
              </HelpTooltip>
            </span>
            <span className="whitespace-nowrap">
              Active subscriptions{' '}
              <strong className="ml-1 font-semibold text-ink">
                {currentState.activeSubscribers}
              </strong>
            </span>
            {subscriptionLifecycle && (
              <span className="inline-flex items-center gap-1 whitespace-nowrap">
                Churned{' '}
                <strong
                  className={cn(
                    'ml-1 font-semibold tabular-nums',
                    subscriptionLifecycle.churned > 0
                      ? 'text-critical'
                      : 'text-ink',
                  )}
                >
                  {subscriptionLifecycle.churned}
                </strong>
                <HelpTooltip label="About subscription churn">
                  {`RevenueCat subscription expirations in ${periodLabel.toLowerCase()}. Access has ended for these subscriptions. RevenueCat also reported ${subscriptionLifecycle.cancellations} cancellation ${subscriptionLifecycle.cancellations === 1 ? 'signal' : 'signals'} and ${subscriptionLifecycle.recovered} ${subscriptionLifecycle.recovered === 1 ? 'recovery' : 'recoveries'}. Cancellation can mean renewal was turned off or a refund, so it is not counted as churn until expiration. A churn rate is not shown because it requires the subscriber count at the start of the period.`}
                </HelpTooltip>
              </span>
            )}
            {currentState.activeTrials > 0 && (
              <span className="whitespace-nowrap">
                Active trials{' '}
                <strong className="ml-1 font-semibold text-ink">
                  {currentState.activeTrials}
                </strong>
              </span>
            )}
          </p>
        ) : (
          <p className="text-ink-muted">{revenueStatus}</p>
        )}
        {syncDetails && (
          <HelpTooltip
            label="Sync details"
            icon={<Clock3 aria-hidden="true" className="size-3.5" />}
          >
            {syncDetails}
          </HelpTooltip>
        )}
      </div>
    </Card>
  );
};

export const GrowthOverview = ({
  projectId,
  analytics,
  showGoals = true,
}: {
  projectId: string;
  analytics: ProjectAnalyticsResponse;
  showGoals?: boolean;
}) => {
  const assistant = useAssistant();
  const [showRevenueDetails, setShowRevenueDetails] = React.useState(false);
  const [selectedGoalId, setSelectedGoalId] = React.useState<string>();
  const integrations = useQuery({
    queryKey: ['integrations', projectId],
    queryFn: () => integrationApi.listIntegrations(projectId),
    refetchInterval: 30_000,
  });
  const sources =
    integrations.data?.integrations.filter(
      (item) => item.status !== 'disconnected' && item.livemode,
    ) ?? [];
  const revenueSources = sources.filter(isRevenueIntegration);
  const source =
    revenueSources.find((item) => item.provider === 'revenuecat') ??
    revenueSources.find((item) => item.provider === 'stripe');
  const range = analytics.range;
  const revenue = useQuery({
    queryKey: [
      'analytics',
      'project',
      projectId,
      'revenue',
      range,
      'live',
      source?.provider,
    ],
    queryFn: () =>
      analyticsApi.getRevenue(projectId, range, 'live', source!.provider),
    enabled: Boolean(source),
    refetchInterval: 30_000,
  });
  const milestones = useQuery({
    queryKey: [
      'analytics',
      'project',
      projectId,
      'milestones',
      analytics.range.start,
      analytics.range.end,
    ],
    queryFn: () => analyticsApi.getMilestones(projectId, analytics.range),
    refetchInterval: 30_000,
    retry: 1,
  });
  const goals = useQuery({
    queryKey: ['project-goals', projectId],
    queryFn: () => projectApi.listGoals(projectId),
  });
  const primaryGoal = milestones.data?.primaryGoal;
  const selectedGoal =
    goals.data?.goals.find((goal) => goal.id === selectedGoalId) ?? primaryGoal;
  const showingPrimaryGoal =
    !selectedGoal || !primaryGoal || selectedGoal.id === primaryGoal.id;
  const selectedGoalAnalytics = useQuery({
    queryKey: [
      'analytics',
      'project',
      projectId,
      'goal',
      selectedGoal?.id,
      analytics.range,
    ],
    queryFn: () =>
      analyticsApi.getGoalAnalytics(
        projectId,
        selectedGoal!.id,
        analytics.range,
      ),
    enabled: Boolean(selectedGoal && !showingPrimaryGoal),
    retry: 1,
  });
  const summary =
    revenue.data?.summary.find((item) => item.currency === 'USD') ??
    revenue.data?.summary[0];
  const currentState =
    revenue.data?.currentState?.find((item) => item.currency === 'USD') ??
    revenue.data?.currentState?.[0] ??
    summary;
  const selectedMilestones = React.useMemo(() => {
    if (showingPrimaryGoal) {
      return milestones.data?.points;
    }
    if (!selectedGoalAnalytics.data) {
      return undefined;
    }
    const revenueByTime = new Map(
      milestones.data?.points.map((point) => [point.timestamp, point]),
    );
    const goalByTime = new Map(
      selectedGoalAnalytics.data.timeseries.map((point) => [
        point.timestamp,
        point,
      ]),
    );
    return Array.from(new Set([...revenueByTime.keys(), ...goalByTime.keys()]))
      .sort()
      .map((timestamp) => {
        const revenuePoint = revenueByTime.get(timestamp);
        const goalPoint = goalByTime.get(timestamp);
        return {
          timestamp,
          goalConversions: goalPoint?.conversions ?? 0,
          goalCompletions: goalPoint?.totalCompletions ?? 0,
          signups: goalPoint?.conversions ?? 0,
          newPaid: revenuePoint?.newPaid ?? 0,
          payments: revenuePoint?.payments ?? 0,
          recordedRevenueUsd: revenuePoint?.recordedRevenueUsd ?? 0,
        };
      });
  }, [milestones.data?.points, selectedGoalAnalytics.data, showingPrimaryGoal]);
  const points = buildGrowthPoints(
    analytics.project.timeseries,
    selectedMilestones,
    milestones.data?.paidSince ?? null,
    analytics.range.bucketMinutes,
    undefined,
  );
  const explainAnomaly = React.useCallback(
    async (anomaly: GrowthAnomaly) => {
      assistant.open();
      await assistant.send(
        growthAnomalyPrompt(anomaly, analytics.range.bucketMinutes),
      );
    },
    [analytics.range.bucketMinutes, assistant],
  );
  const revenueStatus =
    revenue.data?.periodStatus === 'unsupported'
      ? 'Not available for this range'
      : revenue.isError
        ? 'Revenue could not be loaded'
        : integrations.isError
          ? 'Revenue connections could not be loaded'
          : integrations.isLoading || revenue.isLoading
            ? 'Loading revenue…'
            : source
              ? 'Waiting for provider metrics'
              : 'No revenue source connected';
  const periodLabel = analyticsRangeLabel(range);
  return (
    <div className="space-y-3">
      <GrowthCard
        showRevenueMetrics={Boolean(source)}
        points={points}
        visitors={analytics.project.summary.visitors}
        previousVisitors={analytics.project.summary.previousVisitors}
        bucketMinutes={analytics.range.bucketMinutes}
        revenue={summary}
        currentState={currentState}
        subscriptionLifecycle={
          source?.provider === 'revenuecat'
            ? (revenue.data?.subscriptionLifecycle ?? undefined)
            : undefined
        }
        comparisonEnabled={range.comparisonEnabled !== false}
        revenueStatus={revenueStatus}
        provider={source?.provider === 'stripe' ? 'Stripe' : 'RevenueCat'}
        syncDetails={
          source
            ? `${source.provider === 'stripe' ? 'Stripe' : 'RevenueCat'} · Production${summary?.lastPaymentAt && source.provider === 'revenuecat' ? ` · Metrics refreshed ${formatRelative(summary.lastPaymentAt)}` : ''}`
            : undefined
        }
        paidSince={milestones.data?.paidSince}
        periodLabel={periodLabel}
        revenuePeriodLabel={
          source?.provider === 'revenuecat'
            ? range.key === '24h'
              ? 'Today (UTC)'
              : `${periodLabel} (UTC days)`
            : periodLabel
        }
        milestoneStatus={
          selectedGoalAnalytics.isError
            ? `${selectedGoal?.name ?? 'Goal'} could not be loaded.`
            : selectedGoalAnalytics.isLoading
              ? `Loading ${selectedGoal?.name.toLowerCase() ?? 'goal'}…`
              : milestones.isError
                ? 'Milestones could not be loaded. Traffic is still available; check that the analytics update has been deployed.'
                : milestones.isLoading
                  ? 'Loading goals and paid milestones…'
                  : undefined
        }
        primaryGoal={selectedGoal}
        goalSummary={
          showingPrimaryGoal
            ? milestones.data?.goalSummary
            : selectedGoalAnalytics.data?.summary
        }
        goals={goals.data?.goals}
        selectedGoalId={selectedGoal?.id}
        onGoalChange={setSelectedGoalId}
        onExplainAnomaly={explainAnomaly}
        isExplainingAnomaly={
          assistant.status === 'submitted' || assistant.status === 'streaming'
        }
      />
      {showGoals && (
        <GoalsReport
          projectId={projectId}
          range={range}
          goals={goals.data?.goals ?? []}
          goalsLoading={goals.isLoading}
          goalsError={goals.isError}
          selectedGoalId={selectedGoal?.id}
          onGoalChange={setSelectedGoalId}
        />
      )}
      <div className="flex flex-wrap items-center justify-between gap-2 px-1 text-xs text-ink-muted">
        {!source && (
          <Link
            to={`/projects/${projectId}/data-sources`}
            className="text-accent hover:underline"
          >
            Connect a revenue source
          </Link>
        )}
        {milestones.isError && (
          <button
            type="button"
            onClick={() => void milestones.refetch()}
            className="inline-flex items-center gap-1 text-accent"
          >
            <RefreshCw className="size-3" />
            Retry milestones
          </button>
        )}
      </div>
      {source && (
        <button
          type="button"
          aria-expanded={showRevenueDetails}
          onClick={() => setShowRevenueDetails(!showRevenueDetails)}
          className="text-xs text-ink-muted hover:text-ink"
        >
          {showRevenueDetails ? 'Hide revenue details' : 'Revenue details'}
        </button>
      )}
      {showRevenueDetails && (
        <RevenueOverview projectId={projectId} range={range} />
      )}
    </div>
  );
};
